import React from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight, Stethoscope, Clock, Brain, Activity, Pill, FileText, Heart, Thermometer, Eye, Ear, Baby, Users, FlaskConical, ClipboardList, Shield, CheckCircle } from 'lucide-react';
import { mockServices } from '../mockData';

const Services = () => {
  const allServices = [
    ...mockServices,
    {
      id: 7,
      title: "Women's Health",
      description: "Comprehensive care for women's health needs including birth control, UTIs, and routine concerns.",
      icon: "Heart"
    },
    {
      id: 8,
      title: "Pediatric Care",
      description: "Expert care for children including sick visits, rashes, and minor injuries.",
      icon: "Baby"
    },
    {
      id: 9,
      title: "Allergy Care",
      description: "Diagnosis and treatment for seasonal allergies, food allergies, and allergic reactions.",
      icon: "Thermometer"
    },
    {
      id: 10,
      title: "Eye & Ear Care",
      description: "Treatment for pink eye, ear infections, and other common eye and ear conditions.",
      icon: "Eye"
    },
    {
      id: 11,
      title: "Skin Conditions",
      description: "Diagnosis and treatment for acne, rashes, eczema, and other dermatological concerns.",
      icon: "Users"
    },
    {
      id: 12,
      title: "Preventive Care",
      description: "Wellness visits, health screenings, and preventive health guidance.",
      icon: "Stethoscope"
    }
  ];

  const iconMap = {
    Stethoscope: Stethoscope,
    Clock: Clock,
    Brain: Brain,
    Activity: Activity,
    Pill: Pill,
    FileText: FileText,
    Heart: Heart,
    Baby: Baby,
    Thermometer: Thermometer,
    Eye: Eye,
    Ear: Ear,
    Users: Users,
    FlaskConical: FlaskConical,
    ClipboardList: ClipboardList
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F4F6F8] to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#12324A] mb-6">
            Our Services
          </h1>
          <p className="text-xl text-[#5F6C7B] max-w-3xl mx-auto mb-8">
            Comprehensive healthcare services from licensed providers. From urgent care to ongoing management, we're here to help.
          </p>
          <Button
            size="lg"
            className="bg-[#0F4C81] hover:bg-[#12324A] text-white px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
          >
            Start a Visit Now
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      {/* Medicaid Acceptance Banner */}
      <section className="py-12 bg-gradient-to-r from-[#0066FF] to-[#FF0099]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between text-white">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="text-[#0066FF]" size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-1">We Accept Medicaid</h3>
                <p className="text-white/90">Available in select states. Check your eligibility today.</p>
              </div>
            </div>
            <Button
              size="lg"
              className="bg-white text-[#0066FF] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-xl hover:shadow-2xl flex-shrink-0"
            >
              Check Medicaid Coverage
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allServices.map((service, index) => {
              const Icon = iconMap[service.icon] || Stethoscope;
              const gradients = [
                'from-[#0066FF] to-[#00A3FF]',
                'from-[#FF0099] to-[#FF66CC]',
                'from-[#0066FF] via-[#0099FF] to-[#FF0099]',
                'from-[#FF0099] via-[#CC0099] to-[#0066FF]',
                'from-[#00A3FF] to-[#0066FF]',
                'from-[#FF66CC] to-[#FF0099]',
                'from-[#0099FF] to-[#0066FF]',
                'from-[#CC0099] to-[#FF0099]',
                'from-[#0066FF] to-[#FF0099]',
                'from-[#FF0099] to-[#0066FF]',
                'from-[#00A3FF] via-[#0066FF] to-[#FF0099]',
                'from-[#FF66CC] via-[#FF0099] to-[#0066FF]'
              ];
              return (
                <div
                  key={service.id}
                  className="bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-[#0066FF] hover:shadow-xl transition-all duration-300 group"
                >
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${gradients[index % gradients.length]} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1A202C] mb-4">{service.title}</h3>
                  <p className="text-[#4A5568] leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Treat Section */}
      <section className="py-20 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              Common Conditions We Treat
            </h2>
            <p className="text-xl text-[#5F6C7B] max-w-2xl mx-auto">
              Our providers can diagnose and treat hundreds of conditions
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Cold & Flu", "COVID-19", "Sinus Infections", "Strep Throat",
              "Bronchitis", "Ear Infections", "Pink Eye", "UTIs",
              "Skin Rashes", "Allergies", "Asthma", "Diabetes Management",
              "High Blood Pressure", "Anxiety", "Depression", "Insomnia",
              "Heartburn/GERD", "IBS", "Migraines", "Back Pain",
              "Joint Pain", "Sports Injuries", "Birth Control", "STD Testing"
            ].map((condition, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 border border-gray-200 hover:border-[#1FB6B2] hover:shadow-md transition-all duration-300 flex items-center space-x-3"
              >
                <div className="w-2 h-2 rounded-full bg-[#1FB6B2]"></div>
                <span className="text-[#5F6C7B] font-medium">{condition}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-lg text-[#5F6C7B] mb-6">
              Don't see your condition listed? Our providers can help with many more health concerns.
            </p>
            <Button
              size="lg"
              className="bg-[#0F4C81] hover:bg-[#12324A] text-white px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
            >
              Ask a Provider
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Not For Emergencies Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-200">
            <h3 className="text-2xl font-bold text-[#12324A] mb-4 flex items-center">
              <svg className="w-6 h-6 mr-2 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Not for Medical Emergencies
            </h3>
            <p className="text-[#5F6C7B] mb-4">
              TouchScreenMD is not designed for medical emergencies. If you're experiencing:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start space-x-2">
                <span className="text-red-600 font-bold">•</span>
                <span className="text-[#5F6C7B]">Chest pain or severe shortness of breath</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-red-600 font-bold">•</span>
                <span className="text-[#5F6C7B]">Signs of stroke (facial drooping, arm weakness, speech difficulty)</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-red-600 font-bold">•</span>
                <span className="text-[#5F6C7B]">Major bleeding or severe injuries</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-red-600 font-bold">•</span>
                <span className="text-[#5F6C7B]">Suicidal thoughts or plans to harm yourself or others</span>
              </li>
            </ul>
            <p className="text-[#12324A] font-bold text-lg">
              Call 911 or go to the nearest emergency room immediately.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0F4C81] to-[#12324A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Need Care Now?
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Connect with a licensed provider and get the care you need in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-[#0F4C81] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
            >
              Start a Visit Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#0F4C81] px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
